import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import { ChevronDown, ChevronRight } from "lucide-react";
import config from "../config";

// Enum TipoValencia do backend (vem como número na API)
const TIPO_VALENCIA = { Saude: 0, Social: 1, Cultura: 2, Geral: 3 };

// Lista leve das valências (id, nome, tipo) para o 3.º nível do menu.
// Se falhar, o menu continua a funcionar só com 2 níveis.
const fetchValenciasMenu = async () => {
  try {
    const res = await fetch(`${config.baseURL}/api/Valencias/menu`);
    if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
    return await res.json();
  } catch (error) {
    console.error("Erro ao carregar as valências do menu:", error);
    return [];
  }
};

const Header = () => {
  const [submenu, setSubmenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  // 3.º nível (ex.: Áreas de Intervenção › Cultura › Praça de Touros)
  const [nestedOpen, setNestedOpen] = useState(null);
  const timeoutRef = useRef(null);
  const navigate = useNavigate();

  const documentTypeToId = {
    Convocatória: 6,
    Estatutos: 7,
    "Folha Informativa": 9,
    Orçamento: 3,
    Organograma: 4,
    Outro: 5,
    "Parecer do Definitório": 4,
    "Plano de Atividades": 7,
    "Regulamento Eleitoral": 8,
    "Relatório e Contas": 5,
    "Prevenção da Corrupção": 12,
  };

  useEffect(() => {
    const fetchMenuData = async () => {
      try {
        const [response, valencias] = await Promise.all([
          fetch(`${config.baseURL}/api/navegacao`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }),
          fetchValenciasMenu(),
        ]);

        if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
        const data = await response.json();

        // Submenus de Áreas de Intervenção (url_back com ?tipoValencia=X) recebem as valências desse tipo
        const addValencias = (submenu) => {
          const tipo = submenu.url_back?.match(/tipoValencia=([^&]+)/)?.[1];
          if (!(tipo in TIPO_VALENCIA)) return submenu;

          const children = valencias
            .filter((v) => v.tipoValencia === TIPO_VALENCIA[tipo])
            .sort((a, b) => a.nome.localeCompare(b.nome))
            .map((v) => ({
              id: `valencia_${v.id}`,
              title: v.nome,
              url_front: `/valencia/${v.id}`,
            }));

          return { ...submenu, children };
        };

        // "visible": false = escondido no BO (Navegação do Site)
        const isVisible = (entry) => entry.visible !== false;

        const sortedMenus = data.menus
          .filter(isVisible)
          .map((item) => {
            if (item.submenus?.length > 0) {
              //Verificar se o submenu está visível e se tem a propriedade "url_front"
              item.submenus = item.submenus
                .filter((submenu) => isVisible(submenu) && submenu.url_front)
                .map(addValencias);
              item.submenus.sort((a, b) => a.title.localeCompare(b.title));
            }
            return item;
          })
          // Menu sem página própria e sem submenus visíveis não aparece
          .filter((item) => item.url_front || item.submenus?.length > 0);

        setMenuItems(sortedMenus);
      } catch (error) {
        console.error("Erro ao carregar o menu:", error);
      }
    };

    fetchMenuData();
  }, []);

  const toggleSubmenu = (key) => {
    setSubmenu(submenu === key ? null : key);
  };

  const handleMouseEnter = (key) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setSubmenu(key);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setSubmenu(null);
      setNestedOpen(null);
    }, 200);
  };

  const handleMobileLinkClick = () => {
    setMobileMenuOpen(false);
    setNestedOpen(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleContactButtonClick = () => {
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmenuClick = (link) => {
    const urlBack = link.url_back;
    // if (!urlBack) return;
    if (urlBack) {
      const match = urlBack.match(/tipoDocumento=([^&]+)/);
      if (match) {
        const tipoDocumento = decodeURIComponent(match[1]);
        const id = documentTypeToId[tipoDocumento];
        if (id !== undefined) {
          navigate(`/documentos/${id}`);
          setSubmenu(null);
          return;
        }
      }
    }

    // fallback to frontend route
    if (link.url_front) {
      navigate(link.url_front);
      setSubmenu(null);
    } else {
      console.warn("No document or frontend route found for:", link);
    }
  };

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-screen-xl mx-auto flex justify-between items-center px-4 py-3 md:py-4">
        <Link to="/" className="flex items-center">
          <img src={logo} alt="Logo" className="h-12" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex space-x-6">
          {menuItems.map((item) => (
            <div
              key={item.id}
              className="relative flex items-center"
              onMouseEnter={() => handleMouseEnter(item.id)}
              onMouseLeave={handleMouseLeave}
            >
              {item.url_front ? (
                <Link
                  to={item.url_front}
                  className="font-semibold text-gray-800 hover:text-orange-600 flex items-center gap-1"
                  onClick={handleMobileLinkClick}
                >
                  {item.title}
                  {item.submenus?.length > 0 && (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </Link>
              ) : item.submenus?.length > 0 ? (
                <button
                  className={`font-semibold text-gray-800 flex items-center gap-1 bg-transparent focus:outline-none focus:ring-0 ${
                    submenu === item.id
                      ? "text-orange-600"
                      : "hover:text-orange-600"
                  }`}
                >
                  {item.title}
                  <ChevronDown className="w-4 h-4" />
                </button>
              ) : (
                <span className="font-semibold text-gray-800">
                  {item.title}
                </span>
              )}

              {submenu === item.id && item.submenus?.length > 0 && (
                <div
                  className="absolute left-0 flex flex-col bg-white border shadow-md mt-2 w-56 z-50"
                  style={{ top: "100%" }}
                >
                  {item.submenus.map((link) => (
                    <div
                      key={link.id}
                      className="relative"
                      onMouseEnter={() =>
                        setNestedOpen(link.children?.length > 0 ? link.id : null)
                      }
                    >
                      <button
                        onClick={() => {
                          handleSubmenuClick(link);
                          setNestedOpen(null);
                        }}
                        className={`text-left px-4 py-2 hover:bg-gray-100 hover:text-orange-600 text-sm no-underline w-full flex justify-between items-center gap-2 ${
                          nestedOpen === link.id
                            ? "bg-gray-100 text-orange-600"
                            : "text-gray-800"
                        }`}
                      >
                        <span>{link.title}</span>
                        {link.children?.length > 0 && (
                          <ChevronRight className="w-4 h-4 shrink-0" />
                        )}
                      </button>

                      {/* 3.º nível: valências desta área */}
                      {nestedOpen === link.id && link.children?.length > 0 && (
                        <div className="absolute left-full top-0 flex flex-col bg-white border shadow-md w-64 z-50 max-h-[70vh] overflow-y-auto">
                          {link.children.map((child) => (
                            <button
                              key={child.id}
                              onClick={() => {
                                handleSubmenuClick(child);
                                setNestedOpen(null);
                              }}
                              className="text-left px-4 py-2 hover:bg-gray-100 hover:text-orange-600 text-gray-800 text-sm no-underline w-full"
                            >
                              {child.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Contact Button */}
        <div className="hidden md:block">
          <Link to="/Contactos">
            <button
              className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition"
              onClick={handleContactButtonClick}
            >
              Contacte-nos
            </button>
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-2xl"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          ☰
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-4 py-2 flex flex-col space-y-2">
            {menuItems.map((item) => (
              <div key={item.id}>
                {item.submenus && item.submenus.length > 0 ? (
                  <>
                    <button
                      className="w-full text-left font-semibold text-gray-800 flex justify-between items-center"
                      onClick={() => toggleSubmenu(item.id)}
                    >
                      {item.title}
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    {submenu === item.id && (
                      <div className="flex flex-col pl-4 mt-1">
                        {item.submenus.map((link) => (
                          <div key={link.id}>
                            <div className="flex justify-between items-center">
                              <button
                                onClick={() => {
                                  handleSubmenuClick(link);
                                  handleMobileLinkClick();
                                }}
                                className="py-1 text-sm text-gray-700 hover:text-orange-600 text-left flex-1"
                              >
                                {link.title}
                              </button>
                              {link.children?.length > 0 && (
                                <button
                                  onClick={() =>
                                    setNestedOpen(
                                      nestedOpen === link.id ? null : link.id
                                    )
                                  }
                                  className="p-1 text-gray-700"
                                  aria-label={`Ver ${link.title}`}
                                  aria-expanded={nestedOpen === link.id}
                                >
                                  <ChevronDown
                                    className={`w-4 h-4 transition-transform ${
                                      nestedOpen === link.id ? "rotate-180" : ""
                                    }`}
                                  />
                                </button>
                              )}
                            </div>
                            {nestedOpen === link.id &&
                              link.children?.length > 0 && (
                                <div className="flex flex-col pl-4 border-l border-gray-200 ml-1">
                                  {link.children.map((child) => (
                                    <button
                                      key={child.id}
                                      onClick={() => {
                                        handleSubmenuClick(child);
                                        handleMobileLinkClick();
                                      }}
                                      className="py-1 text-sm text-gray-600 hover:text-orange-600 text-left"
                                    >
                                      {child.title}
                                    </button>
                                  ))}
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.url_front}
                    className="w-full text-left font-semibold text-gray-800 py-2 hover:text-orange-600"
                    onClick={handleMobileLinkClick}
                  >
                    {item.title}
                  </Link>
                )}
              </div>
            ))}

            <Link to="/Contactos">
              <button
                className="w-full mt-2 bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700"
                onClick={handleContactButtonClick}
              >
                Contacte-nos
              </button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
